import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PageLoader, FullWidthContainer } from '../../components';
import { ExternalCalendars } from './ExternalCalendars';
import { SchedulingCalendarsSection } from './scheduling/components/SchedulingCalendarsSection';
import { useSchedulingAccess, useSchedulingProfiles } from './scheduling/hooks';

type Area = 'scheduling' | 'external';

const AREA_STORAGE_KEY = 'calendars:area';
const AREAS: Area[] = ['scheduling', 'external'];

const isArea = (value: string | null): value is Area => AREAS.includes(value as Area);

const AreaSwitch: React.FC<{ area: Area; onChange: (area: Area) => void }> = ({
  area,
  onChange,
}) => {
  const { t } = useTranslation();
  return (
    <div className="inline-flex overflow-hidden rounded-lg border border-gray-300 dark:border-gray-600">
      {AREAS.map(value => (
        <button
          key={value}
          type="button"
          onClick={() => onChange(value)}
          className={`px-4 py-2 text-sm font-medium ${
            area === value
              ? 'bg-blue-600 text-white'
              : 'bg-white text-gray-700 hover:bg-gray-50 dark:bg-gray-900 dark:text-gray-300 dark:hover:bg-gray-800'
          }`}
        >
          {t(`calendars.areas.${value}`)}
        </button>
      ))}
    </div>
  );
};

// Basic organizations keep the plain external view; Business/Enterprise get the area switch.
const Calendars: React.FC = () => {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const { data: access, isLoading: accessLoading } = useSchedulingAccess();
  const enabled = access?.enabled ?? false;
  const { data: profiles = [], isLoading: profilesLoading } = useSchedulingProfiles(enabled);

  if (accessLoading || (enabled && profilesLoading)) return <PageLoader show />;
  if (!enabled) return <ExternalCalendars />;

  const requested = params.get('area');
  const stored = localStorage.getItem(AREA_STORAGE_KEY);
  const area: Area = isArea(requested)
    ? requested
    : isArea(stored)
      ? stored
      : profiles.length > 0
        ? 'scheduling'
        : 'external';

  const changeArea = (next: Area) => {
    localStorage.setItem(AREA_STORAGE_KEY, next);
    setParams({ area: next }, { replace: true });
  };

  const switcher = <AreaSwitch area={area} onChange={changeArea} />;

  if (area === 'external') return <ExternalCalendars header={switcher} />;

  return (
    <FullWidthContainer>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            {t('calendars.title')}
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">{t('calendars.subtitle')}</p>
        </div>
        {switcher}
        <SchedulingCalendarsSection />
      </div>
    </FullWidthContainer>
  );
};

export { Calendars };
